import { Box, Code, Heading, Text, VStack } from "@chakra-ui/react";
import { Element } from "react-scroll";
import CodeBlock from "./CodeBlock";
import DocsTable from "./DocsTable";

export default function PlacesAutocompleteDocs() {
  return (
    <VStack align="stretch" spacing={10} w="full">
      <Element name="baseurl">
        <Box>
        <Heading as="h2">Base URL</Heading>
        <Text as="p" fontSize="lg" mt={5}>
          <Code wordBreak="break-all">https://api.potterstech.com/v1</Code>
        </Text>
        </Box>
      </Element>

      <Element name="endpoint">
        <Box>
        <Heading as="h2">Endpoint</Heading>
        <Text as="p" fontSize="lg" mt={5}>
          <Code>POST /places/autocomplete</Code>
        </Text>
        <Text as="p" fontSize="lg" mt={5}>
          <Text as="span" fontWeight="bold" textDecoration="none">
            Full URL:
          </Text>{" "}
          <Code wordBreak="break-all">https://api.potterstech.com/v1/places/autocomplete</Code>
        </Text>
        </Box>
      </Element>

      <Element name="authentication">
        <Box>
        <Heading as="h2">Authentication</Heading>
        <Text as="p" fontSize="lg" mt={5}>
          <Code>X-Api-Key: &lt;YOUR_API_KEY&gt;</Code>
        </Text>
        </Box>
      </Element>

      <Element name="headers">
        <Box>
        <Heading as="h2">Headers</Heading>
      <DocsTable
        headers={["Header", "Required", "Description"]}
        rows={[
          ["Content-Type", "yes", "application/json"],
          ["X-Api-Key", "yes", "Your assigned API key"],
        ]}
      />
        </Box>
      </Element>

      <Element name="requestparameters">
        <Box>
        <Heading as="h2" mb={5}>Request Parameters</Heading>
        <Text as="b" mt={5}>Required</Text>
        <DocsTable
          headers={["Parameter", "Type", "Description"]}
          rows={[
            ["input", "string", "Text input used to generate autocomplete suggestions"],
          ]}
        />

        <Text as="b" mt={20}>
          Optional
        </Text>
        <DocsTable
          headers={["Parameter", "Type", "Description"]}
          rows={[
            ["components", "string", "Component filtering, for example country:in"],
            ["language", "string", "Response language. Default: en"],
            ["origin", "object", "Origin location to calculate distance"],
            ["locationBias", "object", "Bias results toward a geographic area"],
            ["locationRestriction", "object", "Restrict results strictly to a geographic area"],
          ]}
        />
        </Box>
      </Element>

      <Element name="locationbias">
        <Box>
        <Heading as="h2">Location Bias & Restriction</Heading>
        <Text as="p" fontSize="lg" mt={5} mb={5}>
          You may specify either <Code>locationBias</Code> or{" "}
          <Code>locationRestriction</Code>.
        </Text>

        <Text as="b" mt={5}>
          Circular
        </Text>
        <CodeBlock containerProps={{ mt: 8 }}>
        {`{
  "circle": {
    "center": { "latitude": 17.415680447110905, "longitude": 78.47500453770152 },
    "radius": { "units": "Meters", "value": 5000 }
  }
}`}
        </CodeBlock>

        <Text as="b" mt={8}>
          Rectangular
        </Text>
        <CodeBlock containerProps={{ mt: 8 }}>
        {`{
  "rectangle": {
    "northeast": { "latitude": 17.43329703236551, "longitude": 78.488302222395 },
    "southwest": { "latitude": 17.390622856047326, "longitude": 78.44499530094147 }
  }
}`}
      </CodeBlock>
        </Box>
      </Element>

      <Element name="requestexamples">
        <Box>
        <Heading as="h2" mb={5}>Request Examples</Heading>
        <Text as="b" mt={5}>
          Simple Request
        </Text>
        <CodeBlock containerProps={{ mt: 8 }}>
        {`{
  "input": "NTR Gardens, Khairatabad, Hyderabad, Telangana, India"
}`}
      </CodeBlock>
        <Text as="b" mt={8}>
          With Circular Location Bias
        </Text>
        <CodeBlock containerProps={{ mt: 8 }}>
        {`{
  "input": "NTR Gardens, Khairatabad, Hyderabad, Telangana, India",
  "locationBias": {
    "circle": {
      "center": { "latitude": 17.415680447110905, "longitude": 78.47500453770152 },
      "radius": { "units": "Meters", "value": 5000 }
    }
  }
}`}
      </CodeBlock>
        <Text as="b" mt={8}>
          With Rectangular Location Restriction
        </Text>
        <CodeBlock containerProps={{ mt: 8 }}>
        {`{
  "input": "NTR Gardens, Khairatabad, Hyderabad, Telangana, India",
  "locationRestriction": {
    "rectangle": {
      "northeast": { "latitude": 17.43329703236551, "longitude": 78.488302222395 },
      "southwest": { "latitude": 17.390622856047326, "longitude": 78.44499530094147 }
    }
  }
}`}
      </CodeBlock>
        <Text as="b" mt={8}>
          With Distance From Origin
        </Text>
        <CodeBlock containerProps={{ mt: 8 }}>
        {`{
  "input": "NTR Gardens, Khairatabad, Hyderabad, Telangana, India",
  "origin": { "latitude": 17.406226281946527, "longitude": 78.46905012485428 }
}`}
      </CodeBlock>
        </Box>
      </Element>

      <Element name="sampleresponses">
        <Box>
        <Heading as="h2" mb={5}>Sample Responses</Heading>
        <Text as="b" mt={5}>
          Simple Response
        </Text>
        <CodeBlock containerProps={{ mt: 8 }}>
        {`{
  "suggestions": [
    {
      "placePrediction": {
        "placeId": "95609768637121397521971935917962",
        "text": {
          "text": "NTR Gardens, Tankbund Road, Tankbund, Hyderabad, Khairatabad, Telangana, India"
        },
        "structuredFormat": {
          "mainText": { "text": "NTR Gardens" },
          "secondaryText": {
            "text": "Tankbund Road, Tankbund, Hyderabad, Khairatabad, Telangana, India"
          }
        },
        "types": [],
        "location": "POINT (78.468738 17.412732)"
      }
    }
    // ...
  ]
}`}
      </CodeBlock>
        <Text as="b" mt={8}>
          With Origin (Distance Included)
        </Text>
        <CodeBlock containerProps={{ mt: 8 }}>
        {`{
  "suggestions": [
    {
      "placePrediction": {
        "placeId": "95609768637121397521971935917962",
        "text": {
          "text": "NTR Gardens, Tankbund Road, Tankbund, Hyderabad, Khairatabad, Telangana, India"
        },
        "structuredFormat": {
          "mainText": { "text": "NTR Gardens" },
          "secondaryText": {
            "text": "Tankbund Road, Tankbund, Hyderabad, Khairatabad, Telangana, India"
          }
        },
        "types": [],
        "location": "POINT (78.468738 17.412732)",
        "distanceMeters": 724.1603347930611
      }
    }
    // ...
  ]
}`}
      </CodeBlock>
        </Box>
      </Element>

      <Element name="responsefields">
        <Box>
        <Heading as="h2" mb={5}>Response Field Reference</Heading>
        <Text as="b" mt={5}>
          suggestions[]
        </Text>
        <DocsTable
          headers={["Field", "Type", "Description"]}
          rows={[["placePrediction", "object", "A suggested place"]]}
        />

        <Text as="b" mt={8}>
          placePrediction
        </Text>
        <DocsTable
          headers={["Field", "Type", "Description"]}
          rows={[
            ["placeId", "string", "Unique place identifier"],
            ["text.text", "string", "Full display text"],
            ["structuredFormat.mainText.text", "string", "Primary place name"],
            ["structuredFormat.secondaryText.text", "string", "Address or context"],
            ["types", "array", "Place categories"],
            ["location", "string", "WKT POINT format (POINT (lon lat))"],
            ["distanceMeters", "number", "Distance from origin if provided"],
          ]}
        />
        </Box>
      </Element>

      <Element name="examplerequests">
        <Box>
        <Heading as="h2" mb={5}>Example Requests</Heading>
        <Text as="b" mt={5}>
          cURL
        </Text>
        <CodeBlock containerProps={{ mt: 8 }} variant="dark">
        {`curl -X POST \\
  'https://api.potterstech.com/v1/places/autocomplete' \\
  -H 'Content-Type: application/json' \\
  -H 'X-Api-Key: YOUR_API_KEY' \\
  -d '{
    "input": "NTR Gardens, Khairatabad, Hyderabad, Telangana, India"
  }'`}
      </CodeBlock>
        <Text as="b" mt={8}>
          JavaScript
        </Text>
        <CodeBlock containerProps={{ mt: 8 }} variant="dark">
        {`const res = await fetch("https://api.potterstech.com/v1/places/autocomplete", {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
    "X-Api-Key": process.env.POTTERS_API_KEY
  },
  body: JSON.stringify({
    input: "NTR Gardens, Khairatabad, Hyderabad, Telangana, India"
  })
});

console.log(await res.json());`}
      </CodeBlock>
        <Text as="b" mt={8}>
          Python
        </Text>
        <CodeBlock containerProps={{ mt: 8 }} variant="dark">
        {`import os, requests, json

url = "https://api.potterstech.com/v1/places/autocomplete"
headers = {
  "Content-Type": "application/json",
  "X-Api-Key": os.environ["POTTERS_API_KEY"]
}
payload = {
  "input": "NTR Gardens, Khairatabad, Hyderabad, Telangana, India"
}

resp = requests.post(url, headers=headers, data=json.dumps(payload))
print(resp.json())`}
      </CodeBlock>
        </Box>
      </Element>

      <Element name="statuscodes">
        <Box>
        <Heading as="h2">Status Codes</Heading>
        <DocsTable
          headers={["Code", "Description"]}
          rows={[
            ["200", "Suggestions returned successfully"],
            ["400", "Invalid request body"],
            ["401", "Invalid or missing API key"],
            ["403", "Access forbidden"],
            ["429", "Rate limit exceeded"],
            ["5xx", "Server error"],
          ]}
        />
        </Box>
      </Element>

      <Element name="notes">
        <Box>
        <Heading as="h2">Notes</Heading>
        <Text as="p" fontSize="lg" mt={5}>
          <ul>
            <li>Use locationBias to influence ranking.</li>
            <li>Use locationRestriction to strictly constrain results.</li>
            <li>Providing origin enables distance calculation and applies
            distance-based relevance to the results.</li>
          </ul>
        </Text>
        </Box>
      </Element>
    </VStack>
  );
}

